import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import api from "../../helpers/util";

const CreateUser = ({ handleClose, companies, roles, userId }) => {
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    companyid: "",
    roleid: 2,
    isactive: true,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [isError, setIsError] = useState(false);

  const [validationErrors, setValidationErrors] = useState({
    email: "",
    password: "",
    firstname: "",
    lastname: "",
  });

  useEffect(() => {
    if (userId) {
      const fetchUser = async () => {
        try {
          const { data } = await api.get(`/users/${userId}`);
          console.log("user fetched:", data);
          setUserForm({
            email: data.user.email || "",
            password: "", // Never populate password
            firstname: data.user.firstname || "",
            lastname: data.user.lastname || "",
            companyid: data.user.companyid || "",
            roleid: data.user.roleid || 2,
            isactive: data.user.isactive !== undefined ? data.user.isactive : true,
          });
        } catch (error) {
          setErrorMessage("Error fetching user details.");
          console.error(error);
        }
      };
      fetchUser();
    }
  }, [userId]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    const newVal = type === "checkbox" ? checked : value;
    
    setUserForm((prevState) => ({
      ...prevState,
      [name]: newVal,
    }));
  };

  const validateUser = () => {
    const errors = {
      email: !userForm.email
        ? "Please enter email"
        : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.email)
        ? "Please enter a valid email address"
        : "",
      password:
        !userId && !userForm.password
          ? "Please enter password"
          : userForm.password && userForm.password.length < 6
          ? "Password must be at least 6 characters"
          : "",
      firstname: !userForm.firstname ? "Please enter first name" : "",
      lastname: !userForm.lastname ? "Please enter last name" : "",
    };
    return errors;
  };

  const handleCreateUser = async () => {
    console.log("Creating user", userForm);
    const validationErrs = validateUser();
    const hasValidationErrors = Object.values(validationErrs).some(
      (errMsg) => errMsg !== ""
    );
    setValidationErrors(validationErrs);

    if (hasValidationErrors) {
      return;
    }

    try {
      const { data } = await api.post("/users", userForm);
      console.log("New user created", data);
      
      setUserForm({
        email: "",
        password: "",
        firstname: "",
        lastname: "",
        companyid: "",
        roleid: 2,
        isactive: true,
      });
      
      setIsError(false);
      setSuccessMessage("User created successfully");
      setShowMessage(true);
      
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.log("error", error);
      setIsError(true);
      setShowMessage(true);
      setErrorMessage(
        error?.response?.data?.message || "Error in creating the user"
      );
    }
  };

  const handleUpdateUser = async () => {
    console.log("Updating user", userForm);
    const validationErrs = validateUser();
    const hasValidationErrors = Object.values(validationErrs).some(
      (errMsg) => errMsg !== ""
    );
    setValidationErrors(validationErrs);

    if (hasValidationErrors) {
      return;
    }

    try {
      // Prepare payload WITHOUT password (password is updated separately)
      const payload = {
        email: userForm.email,
        firstname: userForm.firstname,
        lastname: userForm.lastname,
        companyid: userForm.companyid,
        roleid: userForm.roleid,
        isactive: userForm.isactive,
      };

      // Step 1: Update user details
      const { data } = await api.put(`/users/${userId}`, payload);
      console.log("User updated", data);

      // Step 2: If password was provided, update it using the separate endpoint
      if (userForm.password && userForm.password.trim() !== "") {
        console.log("Updating password...");
        try {
          await api.put(`/users/${userId}/password`, {
            password: userForm.password,
          });
          console.log("Password updated successfully");
        } catch (passwordError) {
          console.error("Password update failed:", passwordError);
          // User details updated but password failed
          setIsError(true);
          setShowMessage(true);
          setErrorMessage(
            "User details updated, but password change failed: " +
              (passwordError?.response?.data?.message || passwordError.message)
          );
          return;
        }
      }

      setIsError(false);
      setSuccessMessage(
        userForm.password && userForm.password.trim() !== ""
          ? "User and password updated successfully"
          : "User updated successfully"
      );
      setShowMessage(true);

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      console.log("error", error);
      setIsError(true);
      setShowMessage(true);
      setErrorMessage(
        error?.response?.data?.message || "Error updating user"
      );
    }
  };

  const handleAlert = (isError) => {
    return (
      <div>
        <Snackbar
          open={showMessage}
          autoHideDuration={2000}
          onClose={() => setShowMessage(false)}
          message="Done"
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          sx={{ "& .MuiSnackbar-root": { bottom: "50px" } }}
        >
          <Alert severity={isError ? "error" : "success"} variant="filled">
            {isError ? errorMessage : successMessage}
          </Alert>
        </Snackbar>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{
          overflow: "auto",
          minHeight: "600px",
        }}
      >
        <DialogTitle>
          <div className="mb-2 ms-3">
            <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>
              {userId ? "Update User" : "Create User"}
            </div>
            {userId && (
              <div style={{ fontSize: "0.85rem", color: "#666", marginTop: 4 }}>
                Email: {userForm?.email}
              </div>
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={userForm.email}
                onChange={handleChange}
                required
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  userId
                    ? "New Password (leave blank to keep current)"
                    : "Password"
                }
                name="password"
                type={showPassword ? "text" : "password"}
                value={userForm.password}
                onChange={handleChange}
                required={!userId}
                error={!!validationErrors.password}
                helperText={
                  validationErrors.password || "Minimum 6 characters"
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstname"
                value={userForm.firstname}
                onChange={handleChange}
                required
                error={!!validationErrors.firstname}
                helperText={validationErrors.firstname}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastname"
                value={userForm.lastname}
                onChange={handleChange}
                required
                error={!!validationErrors.lastname}
                helperText={validationErrors.lastname}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  name="companyid"
                  value={userForm.companyid}
                  onChange={handleChange}
                  label="Company"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="roleid"
                  value={userForm.roleid}
                  onChange={handleChange}
                  label="Role"
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.rolename || role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="isactive"
                    checked={userForm.isactive}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {!userId ? (
            <Button onClick={handleCreateUser} color="primary">
              Create
            </Button>
          ) : (
            <Button onClick={handleUpdateUser} color="primary">
              Update
            </Button>
          )}
          <Button onClick={handleClose} color="primary">
            {userId ? "Close" : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
      {showMessage && handleAlert(isError)}
    </>
  );
};

export default CreateUser;